import { useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import * as Sentry from '@sentry/react'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import {
  CreditCard,
  Gift,
  Info,
  Loader,
  ShieldAlert,
  ShieldCheck,
} from 'lucide-react'
import { useMemo, useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod/v4'
import { BillingDetailsForm } from '@/components/form/billing-details'
import { billingDetailsSchema } from '@/components/form/billing-details/schema'
import {
  CONFIRM_SETUP_INTENT,
  CREATE_SETUP_INTENT,
  ONBOARDING_BILLING_QUERY,
  ONBOARDING_UPDATE_STRIPE_ADDRESS,
} from '@/components/payment-method-gate/gql'
import { ProgressView } from '@/components/sv/progress-view'
import { ToasterContent } from '@/components/sv/toaster-content'
import { H2, H3, Muted } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useIdCompany, useSuspenseQuery, useTheme } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type {
  ConfirmSetupIntentMutation,
  ConfirmSetupIntentMutationVariables,
  CreateSetupIntentMutation,
  CreateSetupIntentMutationVariables,
  OnboardingBillingDetailsQuery,
  OnboardingBillingDetailsQueryVariables,
  OnboardingUpdateStripeAddressMutation,
  OnboardingUpdateStripeAddressMutationVariables,
} from '@/components/payment-method-gate/gql.types.gen'
import type { StepStatus } from '@/components/sv/progress-view'

const onboardingSchema = billingDetailsSchema.and(
  z.object({
    cardName: z.string().min(1, 'Card holder name is required'),
  })
)

type OnboardingFormValues = z.input<typeof onboardingSchema>

type StepId = 'billing' | 'init' | 'verify' | 'save'

type StepStatuses = Record<StepId, StepStatus>

const PROGRESS_STEPS = [
  { id: 'billing', label: 'Saving billing details' },
  { id: 'init', label: 'Initializing payment method' },
  { id: 'verify', label: 'Verifying card' },
  { id: 'save', label: 'Attaching payment method' },
]

const INITIAL_STEP_STATUSES: StepStatuses = {
  billing: 'pending',
  init: 'pending',
  verify: 'pending',
  save: 'pending',
}

type Props = {
  isSuperadmin: boolean
  onSkip: () => void
  onSuccess: () => void
}

export const OnboardingWizard = ({
  isSuperadmin,
  onSkip,
  onSuccess,
}: Props) => {
  const { idCompany } = useIdCompany()
  const { theme } = useTheme()
  const stripe = useStripe()
  const elements = useElements()
  const [isPending, startTransition] = useTransition()
  const [isProcessing, setIsProcessing] = useState(false)
  const [isCardComplete, setIsCardComplete] = useState(false)
  const [cardError, setCardError] = useState<string | null>(null)
  const [stepStatuses, setStepStatuses] = useState<StepStatuses>(
    INITIAL_STEP_STATUSES
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const { data } = useSuspenseQuery<
    OnboardingBillingDetailsQuery,
    OnboardingBillingDetailsQueryVariables
  >(ONBOARDING_BILLING_QUERY, { variables: { idCompany } })

  const [updateStripeAddress] = useMutation<
    OnboardingUpdateStripeAddressMutation,
    OnboardingUpdateStripeAddressMutationVariables
  >(ONBOARDING_UPDATE_STRIPE_ADDRESS)
  const [createSetupIntent] = useMutation<
    CreateSetupIntentMutation,
    CreateSetupIntentMutationVariables
  >(CREATE_SETUP_INTENT)
  const [confirmSetupIntent] = useMutation<
    ConfirmSetupIntentMutation,
    ConfirmSetupIntentMutationVariables
  >(CONFIRM_SETUP_INTENT, {
    refetchQueries: ['CompanyBilling', 'PaymentMethods'],
  })

  const isDark = useMemo(() => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
    }
    return theme === 'dark'
  }, [theme])

  const cardElementOptions = useMemo(
    () => ({
      style: {
        base: {
          color: isDark ? '#f4f4f5' : '#09090b',
          '::placeholder': {
            color: isDark ? '#71717a' : '#a1a1aa',
          },
        },
      },
    }),
    [isDark]
  )

  const stripeAddress = data?.company?.sevallaBilling?.address

  const form = useForm<OnboardingFormValues>({
    resolver: zodResolver(onboardingSchema),
    defaultValues: {
      cardName: '',
      name: stripeAddress?.name ?? '',
      country: stripeAddress?.country ?? '',
      state: stripeAddress?.state ?? '',
      city: stripeAddress?.city ?? '',
      zip: stripeAddress?.postalCode ?? '',
      street: stripeAddress?.line1 ?? '',
      street2: stripeAddress?.line2 ?? '',
    },
  })

  const createPaymentMethodObject = async (cardName: string) => {
    const cardElement = elements?.getElement(CardElement)
    if (!stripe || !cardElement) {
      throw new Error('Stripe is not ready')
    }
    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: { name: cardName },
    })
    const idPaymentMethod = paymentMethod?.id ?? ''
    if (!idPaymentMethod && error) {
      const errorCode = error.decline_code || error.code
      throw new Error(errorCode)
    }
    if (!idPaymentMethod) {
      throw new Error('card_declined')
    }
    return idPaymentMethod
  }

  const runStep = async <T,>(id: StepId, fn: () => Promise<T>): Promise<T> => {
    setStepStatuses((prev) => ({ ...prev, [id]: 'active' }))
    try {
      const result = await fn()
      setStepStatuses((prev) => ({ ...prev, [id]: 'complete' }))
      return result
    } catch (e) {
      setStepStatuses((prev) => ({ ...prev, [id]: 'error' }))
      throw e
    }
  }

  const resetProgress = () => {
    setIsProcessing(false)
    setStepStatuses(INITIAL_STEP_STATUSES)
    setErrorMessage(null)
  }

  const onSubmit = async (values: OnboardingFormValues) => {
    const isValid = await form.trigger()
    if (isPending || !isValid) {
      return
    }

    if (!stripe || !isCardComplete) {
      if (!isCardComplete) {
        setCardError('Please enter valid card details')
      }
      return
    }

    setIsProcessing(true)
    setStepStatuses({ ...INITIAL_STEP_STATUSES, billing: 'active' })
    setErrorMessage(null)

    startTransition(async () => {
      try {
        await runStep('billing', async () => {
          await updateStripeAddress({
            variables: {
              idCompany,
              address: {
                name: values.name,
                city: values.city,
                country: values.country,
                line1: values.street,
                line2: values.street2 ?? '',
                postalCode: values.zip,
                state: values.state,
              },
            },
          })
        })

        const { clientSecret, idPaymentMethod } = await runStep(
          'init',
          async () => {
            const idPaymentMethod = await createPaymentMethodObject(
              values.cardName
            )
            const { data } = await createSetupIntent({
              variables: {
                idCompany,
                idPaymentMethod,
                setAsDefault: true,
              },
            })
            const clientSecret = data?.createStripeSetupIntent ?? ''
            if (!clientSecret) {
              throw new Error('Failed to initialize payment method')
            }
            return { clientSecret, idPaymentMethod }
          }
        )

        const idSetupIntent = await runStep('verify', async () => {
          const confirmResult = await stripe?.confirmCardSetup(clientSecret, {
            payment_method: idPaymentMethod,
          })
          if (!confirmResult?.setupIntent || confirmResult.error) {
            throw new Error(
              confirmResult?.error?.message ?? 'Card verification failed'
            )
          }
          return confirmResult.setupIntent.id
        })

        await runStep('save', async () => {
          await confirmSetupIntent({
            variables: {
              idCompany,
              idSetupIntent,
            },
          })
        })

        toast(
          <ToasterContent variant="success">
            Setup complete. Welcome to Sevalla!
          </ToasterContent>,
          { position: 'top-right' }
        )
        onSuccess()
      } catch (e) {
        Sentry.captureException(e)
        setErrorMessage(
          e instanceof Error && e.message
            ? e.message
            : 'Something went wrong. Please try again.'
        )
      }
    })
  }

  return (
    <div className="pb-25">
      <Card className="py-0 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr]">
          <div className="p-6 lg:p-8 lg:border-r grid [grid-template-areas:'stack'] overflow-hidden">
            <div
              className={cn(
                '[grid-area:stack] transition-all duration-300 ease-in-out',
                isProcessing
                  ? 'opacity-0 pointer-events-none -translate-x-8'
                  : 'opacity-100 translate-x-0 delay-150'
              )}
            >
              <div className="mb-8">
                <H2 className="text-pretty">Last step before using Sevalla</H2>
                <p className="mt-2 text-muted-foreground text-pretty">
                  We use your payment method to verify your identity and prevent
                  abuse. You won&apos;t be charged until you start using paid
                  resources. Billing details appear on your invoices.
                </p>
              </div>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="space-y-6"
                >
                  <H3>Payment method</H3>
                  <FormField
                    control={form.control}
                    name="cardName"
                    disabled={isPending}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Card holder name</FormLabel>
                        <FormControl>
                          <Input
                            autoComplete="off"
                            aria-label="Card holder name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormItem>
                    <FormLabel>Card details</FormLabel>
                    <FormControl>
                      <div>
                        <Input type="hidden" />
                        <CardElement
                          className="w-full h-9 border px-2 py-2 rounded-md"
                          options={cardElementOptions}
                          onChange={(event) => {
                            setIsCardComplete(event.complete)
                            setCardError(
                              event.error ? event.error.message : null
                            )
                          }}
                        />
                      </div>
                    </FormControl>
                    {cardError ? (
                      <p className="text-sm text-destructive">{cardError}</p>
                    ) : null}
                  </FormItem>
                  <H3>Billing details</H3>
                  <BillingDetailsForm isDisabled={isPending} />
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={isPending}
                  >
                    {isPending ? (
                      <Loader className="animate-spin" aria-hidden="true" />
                    ) : (
                      <CreditCard />
                    )}
                    Complete setup
                  </Button>
                </form>
              </Form>
            </div>
            <div
              className={cn(
                '[grid-area:stack] transition-all duration-300 ease-in-out',
                isProcessing
                  ? 'opacity-100 translate-x-0 delay-150'
                  : 'opacity-0 pointer-events-none translate-x-8'
              )}
            >
              <ProgressView
                steps={PROGRESS_STEPS}
                statuses={stepStatuses}
                errorMessage={errorMessage}
                onRetry={resetProgress}
              />
            </div>
          </div>
          <div className="hidden lg:flex lg:flex-col p-8">
            <H2 className="mb-8 text-pretty">What to expect</H2>
            <div className="space-y-6">
              <InfoBlock
                icon={ShieldCheck}
                title="No charge today"
                description="We securely verify your card with Stripe to confirm it's valid. You won't be charged until you start using paid resources."
              />
              <InfoBlock
                icon={Gift}
                title="$20 free credit"
                description="Once setup is complete, we add $20 of free credit to your account. Credits are always used before your payment method is charged."
              />
              <InfoBlock
                icon={ShieldAlert}
                title="Fair use policy"
                description="We continuously monitor all resources. Phishing, fraud, crypto miners, and similar abuse are removed without notice and the account is banned. Fraud is reported to the relevant local authorities."
              />
              <InfoBlock
                icon={Info}
                title="Need help?"
                description={
                  <>
                    Our support team is available if you run into anything
                    during setup. Check out the{' '}
                    <a
                      href="https://docs.sevalla.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline underline-offset-4 hover:text-primary/80"
                    >
                      docs
                    </a>{' '}
                    or{' '}
                    <a
                      href="https://api-docs.sevalla.com/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline underline-offset-4 hover:text-primary/80"
                    >
                      API docs
                    </a>{' '}
                    for more details.
                  </>
                }
              />
            </div>
            {isSuperadmin ? (
              <div className="mt-auto pt-6 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground"
                  onClick={onSkip}
                >
                  Skip (superadmin)
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </Card>
    </div>
  )
}

type InfoBlockProps = {
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: React.ReactNode
}

const InfoBlock = ({ icon: Icon, title, description }: InfoBlockProps) => {
  return (
    <div className="flex gap-3">
      <div
        aria-hidden="true"
        className="size-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0"
      >
        <Icon className="size-4.5 text-primary" />
      </div>
      <div className="flex flex-col gap-1 min-w-0">
        <div className="font-medium">{title}</div>
        <Muted>{description}</Muted>
      </div>
    </div>
  )
}
